import { when } from "mobx";
import { makeApi, URLS } from "Utils/apiURL";
import Utils from "Utils/utils";
import InboxUtils from "Utils/InboxUtils";
import SequenceUtils from "Utils/SequenceUtils";
import contactStoreData from "Stores/contactData";
import { settingsStoreData } from "Stores/settingsData";
import { toasterState } from "Components/common/toaster";
import { confirmationPopupState } from "Components/common/confirmationPopup";
import { emailPreviewContentPopupState } from "Components/PreviewPopup/EmailPreviewContentPopup";
import { missingFieldsValidationPopupState } from "Components/ContactCard/MissingFieldsValidationPopup";
import { addSequenceInContactPopupState } from "Pages/Dashboard/contacts/addSeqInContactPopup";
import customListState from "Pages/Dashboard/dataSearch/CustomList/state";
import { buildAddToDirectoryPayload } from "Pages/Dashboard/Copilot/agent/contactActions";
import { draftBodyHtml, offDomain } from "./signalMeta";

/**
 * The app's own single-contact flows, driven from a feed row: the global add-to-sequence picker, the
 * contact page's enrolment checks, the bulk add-to-list call and the one-off email composer.
 * The popups are global singletons mounted by the dashboard, so they are opened, not rebuilt.
 */

const failed = () => toasterState.setToastMsg("Something went wrong. Please try again later", "failure");

// Enrols one contact the way the contact card does: daily-limit and field checks first, then schedule.
const enrol = async (contactId, sequenceId) => {
  const payload = { totalCount: 1, all: true, contactIds: [contactId], sequenceId };
  const res = await contactStoreData.validateDailyLimitForContacts(payload);
  if (!res || !Object.keys(res).length) return false;
  const contacts = res.contacts || [];
  const fields = res.fieldValidationResponse || {};
  const schedule = async () => {
    const response = await Utils.scheduleSequenceForContacts(settingsStoreData.settingsData, Utils.getBulkSequencePayload(contacts, true), sequenceId, []);
    const code = +((response && response.data && response.data.responseCode) || 500);
    const status = (response && response.response && response.response.status) || 0;
    if (code === 200) {
      toasterState.setToastMsg("Contact added to the sequence", "success");
      return true;
    }
    if (code === 105) SequenceUtils.showNoInboxconnectedPopup();
    else if (code === 509) Utils.showContactsUploadLimitReached();
    else if (status === 426) Utils.showUpgradePopup("contacts", true, true);
    else failed();
    return false;
  };
  if ((fields.missingFields || []).length > 0 || !fields.validCount) {
    // The contact page's own "missing fields" popup decides; it resolves only if they skip and add.
    return new Promise(resolve => {
      missingFieldsValidationPopupState.setPopupValues({
        fieldResponse: { ...fields, contacts },
        data: { ...res, payload },
        title: "Add Contact to sequence",
        skipAndAddContactCbk: async () => resolve(await schedule()),
        close: () => {
          missingFieldsValidationPopupState.resetPopup();
          contactStoreData.setValidateContactData("");
          resolve(false);
        }
      });
      missingFieldsValidationPopupState.setShowPopup(true);
    });
  }
  return schedule();
};

/**
 * Opens the sequence picker; resolves to {id, name} once the contact is enrolled, else null. The shared popup only
 * calls back on a pick, so its closing (X, or any path that hides it without picking) resolves null too.
 */
export const addToSequence = contactId =>
  new Promise(resolve => {
    let picking = false;
    addSequenceInContactPopupState.setCallback(async (picked = {}, sequenceId = "") => {
      picking = true;
      if (!sequenceId) return resolve(null);
      resolve((await enrol(contactId, sequenceId)) ? { id: sequenceId, name: (picked && picked.title) || "a sequence" } : null);
    });
    addSequenceInContactPopupState.setPageType("contacts");
    addSequenceInContactPopupState.setShowPopup(true);
    // The pick callback runs before the popup hides itself, so a close that follows a pick leaves the promise to it.
    when(() => !addSequenceInContactPopupState.showPopup, () => !picking && resolve(null));
  });

/** The list picker reads the member's lists from here; loads them once. */
export const ensureContactLists = () => ((customListState.totalContactList || []).length ? Promise.resolve() : customListState.getContactList());

export const listName = id => ((customListState.totalContactList || []).find(l => l.id === id) || {}).name || "a list";

/** Adds one contact to a list with the Contacts page's bulk call. Resolves true when it went in. */
export const addToList = async (contactId, listId) => {
  const res = await makeApi({
    url: URLS.performContactsBulkOperation,
    method: "POST",
    data: { source: "CONTACT", type: "ADD_TO_LIST", totalCount: 1, filter: { contactIds: [contactId] }, search: {}, data: { listId } }
  });
  const data = (res && res.data) || {};
  if (data.activeJob) {
    SequenceUtils.showBulkJobConflictToast("An add-to-list");
    return false;
  }
  if (!data.success) {
    failed();
    return false;
  }
  customListState.getContactList();
  toasterState.setToastMsg(`Added to ${listName(listId)}`, "success");
  return true;
};

/**
 * The one-off email composer addressed to this person, as the header and contact card open it. False when no inbox.
 * `prefill` ({subject, body}) fills in a suggested draft above the signature.
 */
export const draftEmail = async (toEmail, history, prefill = {}) => {
  const fromEmail = Utils.isAccountConnected() && InboxUtils.getPrimaryInboxEmail();
  if (!fromEmail) {
    Utils.showAddAccountPopup({ history }, "Please connect your email account to send emails", () => emailPreviewContentPopupState.setResetPopup());
    return false;
  }
  const settings = settingsStoreData.settingsData || {};
  const bcc = settings.insertBcc && settings.bccEmail ? [{ email: settings.bccEmail }] : [];
  let validated = {};
  const status = await Utils.validateEmailAddressStatus([...Utils.getEmailList({ bcc }), toEmail]);
  if (status && Object.keys(status).length) validated = Utils.addEmailStatus({ bcc, to: [{ email: toEmail }] }, status);
  const popup = emailPreviewContentPopupState.popupValues;
  emailPreviewContentPopupState.setEditorInputValue({
    emailType: "ONE_OFF_EMAIL",
    ...popup,
    emailData: {
      ...popup.emailData,
      from: fromEmail,
      to: [{ email: toEmail }],
      ...validated,
      ...(prefill.subject ? { subject: prefill.subject } : {}),
      content: prefill.body
        ? Utils.insertSignature(fromEmail, true, draftBodyHtml(prefill.body))
        : Utils.addEmptySpaceAboveThreadEmail(Utils.insertSignature(fromEmail, true))
    },
    localState: { ...emailPreviewContentPopupState.editorInputValue.localState, showBccInputAddress: bcc.length > 0, editorInsertCounter: 0 }
  });
  emailPreviewContentPopupState.setshowEditorComposePopup(true);
  emailPreviewContentPopupState.setShowPopup(true);
  return true;
};

/** A contact's saved email, from the contact page's own detail call ("" when they have none). */
export const contactEmail = async contactId => {
  const res = await makeApi({ url: URLS.getContactDetail.replace("<<contactId>>", contactId), method: "GET" });
  const data = (res && res.data) || {};
  return (data.contact || data).email || "";
};

// Resolves true when the rep still wants an email that isn't at the account's domain (they may have moved on).
const confirmOffDomain = (at, domain) =>
  new Promise(resolve => {
    confirmationPopupState.setPopupValues({
      content: `This email is at ${at}, not ${domain}. They may have moved companies. Use it anyway?`,
      actionBtnText: "Use it",
      needCancelBtn: true,
      cancelBtnText: "Cancel",
      callback: () => resolve(true),
      cancelCbk: () => resolve(false)
    });
    confirmationPopupState.setShowPopup(true);
  });

/**
 * Opens the composer with a suggested draft for `who` ({email, contactId, linkedinUrl, name, title, company, domain}):
 * a contact's saved email first, else a lookup from their LinkedIn page (1 credit, only for someone not saved yet).
 * An email off the account's `domain` (or `aliasDomains`) is confirmed first. Resolves who it went to ({...who, email, contactId}) or null.
 */
export const reviewDraft = async (draft, who, history) => {
  let person = { ...who };
  if (!person.email && person.contactId) person.email = await contactEmail(person.contactId);
  if (!person.email && !person.contactId && person.linkedinUrl) {
    // eslint-disable-next-line no-use-before-define
    const hit = await findEmailFromLinkedIn(person);
    if (!hit) return null;
    person = { ...person, ...hit };
  }
  if (!person.email) {
    toasterState.setToastMsg("We don't have an email for them yet.", "failure");
    return null;
  }
  const at = offDomain(person.email, person.domain, person.aliasDomains);
  if (at && !(await confirmOffDomain(at, person.domain))) return null;
  return (await draftEmail(person.email, history, { subject: draft && draft.subject, body: draft && draft.body })) ? person : null;
};

/** The label for opening a draft to `who`: the credit is for finding their email first; sending itself is free. */
export const reviewLabel = who => (who && !who.email && !who.contactId && who.linkedinUrl ? "Find email & send · 1 credit" : "Send draft");

// The app's single-contact lookup by LinkedIn page (List Builder's "Find email" / "Find phone"); it also saves them as a contact.
const lookUpFromLinkedIn = async (type, { name, title, linkedinUrl, company, domain }) => {
  const [firstName, ...rest] = String(name || "").trim().split(/\s+/);
  const res = await makeApi({
    url: URLS.findEmailFromLinkedIn,
    method: "POST",
    data: {
      type,
      linkedInProfileUrl: linkedinUrl,
      fullName: name || undefined,
      firstName: firstName || undefined,
      lastName: rest.join(" ") || undefined,
      experience: [{ title: title || "", companyName: company || "", domain: domain || "", activelyWorking: true }]
    }
  });
  const data = (res && (res.data || (res.response && res.response.data))) || {};
  const code = data.responseCode || (res && (res.status || (res.response && res.response.status)));
  return { data, code, ok: [200, 201, 202].includes(code), contact: data.contact || {} };
};
const usedLine = n => (n > 0 ? ` ${n} credit${n === 1 ? "" : "s"} used.` : "");

/**
 * Finds a person's work email from their LinkedIn page with the app's single-contact lookup (List Builder's "Find
 * email"), which also saves them as a contact. Resolves {email, contactId} when found, else null (and says why).
 */
export const findEmailFromLinkedIn = async person => {
  const { data, code, ok, contact } = await lookUpFromLinkedIn("EMAIL", person);
  if (ok && contact.email) {
    toasterState.setToastMsg(`Email found.${usedLine(data.creditsUsed)}`, "success");
    return { email: contact.email, contactId: contact.id || data.newContactId || null };
  }
  toasterState.setToastMsg(code === 204 ? "You've used all your credits this month." : "We couldn't find their email.", "failure");
  return null;
};

/** Same lookup for a mobile number (10 credits, only when one is found). Resolves {phone, contactId} or null. */
export const findPhoneFromLinkedIn = async person => {
  const { data, code, ok, contact } = await lookUpFromLinkedIn("PHONE", person);
  const phone = Array.isArray(contact.mobilePhone) ? (contact.mobilePhone[0] || {}).number : contact.mobilePhone;
  if (ok && phone) {
    toasterState.setToastMsg(`Phone found.${usedLine(data.creditsUsed)}`, "success");
    return { phone, contactId: contact.id || data.newContactId || null };
  }
  toasterState.setToastMsg(code === 205 || code === 204 ? "You don't have enough credits for a phone number. 1 number uses 10 credits." : "We couldn't find their phone number.", "failure");
  return null;
};

/**
 * Saves someone who is not a contact yet straight into a list, the way List Builder's "Add to list" does (by their
 * LinkedIn page, no email revealed, counted against the plan's contact adds). Resolves true when they went in.
 */
// The directory add behind both below. Someone already saved still counts (a list add puts them in the list).
// Resolves {contactId, existing} or null (and says why).
const saveToDirectory = async (person, listId, { company, domain } = {}) => {
  const [firstName, ...rest] = String(person.name || "").trim().split(/\s+/);
  const payload = buildAddToDirectoryPayload([{ firstName, lastName: rest.join(" "), title: person.title, company, domain, linkedIn: person.linkedinUrl }], listId);
  if (!payload.contacts.length) {
    failed();
    return null;
  }
  const res = await makeApi({ url: URLS.addContactsToDirectory, method: "POST", data: payload });
  const body = (res && (res.response ? res.response.data : res.data)) || {};
  const status = (res && res.response && res.response.status) || body.responseCode;
  if (+status === 402 || +body.responseCode === 402) {
    // List Builder's own upgrade popup; required here, not at the top, since it pulls in the whole paywall.
    const { PaywallUtils } = require("Utils/PaywallUtils"); // eslint-disable-line global-require
    const { userDetail } = require("Stores/userDetail"); // eslint-disable-line global-require
    const { planName = "", inFreePlan = false } = (userDetail && userDetail.userFeatureFlag) || {};
    PaywallUtils.upgradeUtilsFunction("contactsAdded", false, planName, inFreePlan);
    return null;
  }
  if (res && res.response) {
    failed();
    return null;
  }
  const existing = body.existingContacts > 0;
  const contactId = (body.contactIds || [])[0] || null;
  if (!(body.validContacts > 0)) {
    toasterState.setToastMsg("We couldn't add them. Try again.", "failure");
    return null;
  }
  return { contactId, existing };
};

export const addPersonToList = async (person, listId, ctx = {}) => {
  const added = await saveToDirectory(person, listId, ctx);
  if (!added) return false;
  customListState.getContactList();
  toasterState.setToastMsg(`Added to ${listName(listId)}`, "success");
  return true;
};

/**
 * "Add to Salesgear": saves someone from their LinkedIn page as a contact, no list and no email reveal (counted
 * against the plan's contact adds, like List Builder). Resolves their contact id (theirs already, if they were saved), or null.
 */
export const addToSalesgear = async (person, ctx = {}) => {
  const added = await saveToDirectory(person, null, ctx);
  if (!added) return null;
  toasterState.setToastMsg(added.existing ? "Already in your contacts." : `${person.name || "They"} added to Salesgear.`, "success");
  return added.contactId;
};

